import { useState, useCallback, useEffect } from "react";
import { atom, useRecoilState } from "recoil";
import { IService, ServiceOrder } from "src/domains";
import {
  queryGetServices,
  mutationServiceDislike,
  mutationServiceLike,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import {
  OfferLikeToggleEvent,
  offerLikeToggleEmit,
  TabChangeEvent,
  LogoutEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";

export const likeState = atom<IService[]>({
  key: "likeState",
  default: [],
});

export const useLike = () => {
  const [services, setServices] = useRecoilState<IService[]>(likeState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetServices({
        filter: {
          isLiked: true,
          isBlocked: false,
          isDisabled: false,
        },
        order: ServiceOrder.SERVICE_UPDATED,
      });
      if (_isMount) {
        setServices(response?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [setServices]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange, refresh]);

  useEffect(() => {
    const offerLikeToggleListener = EventRegister.addEventListener(
      OfferLikeToggleEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "MyPage") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(offerLikeToggleListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setServices([]);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [services, loading, error, refresh] as const;
};

export const useLikeActions = () => {
  const like = useCallback(async (id: string) => {
    await mutationServiceLike(id);
    offerLikeToggleEmit(id);
  }, []);

  const dislike = useCallback(async (id: string) => {
    await mutationServiceDislike(id);
    offerLikeToggleEmit(id);
  }, []);

  return { like, dislike } as const;
};
